/**
 * Builds the matrix/transposed view layouts sent to the Word export endpoint.
 *
 * Matrix and transposed views are pivoted purely on the frontend, so the backend (which only sees
 * the raw, un-pivoted data) can't reproduce them. Here we run the same pivot the on-screen view
 * uses (`getMatrixData` / `getTransposedData`) and pass the resulting arrangement to the backend,
 * which formats the referenced cell values and draws the tables. Only the pivot arrangement lives
 * on the frontend; value formatting stays on the backend.
 *
 * PERFORMANCE — this awaits a full data load before the export request fires:
 * - Only MATRIX and TRANSPOSE embeds incur it. Every other embed costs a single lightweight
 *   `getTableView` metadata call (used only to check the view type), and a document with no
 *   matrix/transposed embeds adds effectively nothing.
 * - For a qualifying view, `loadSpreadsheetDataForView` streams the view's ENTIRE result set into
 *   the browser (via the same `getSpreadsheetRowsStream` the mounted spreadsheet uses) so the pivot
 *   can be computed. The added delay is therefore roughly "how long that view takes to open" —
 *   a few seconds for a large view, potentially ~10-30s for one over ~1M rows (network transfer +
 *   msgpack decode + column-store build). Such views load with bounded concurrency
 *   (`MAX_CONCURRENT_VIEW_LOADS`) so several large ones can't all buffer in memory at once.
 * - This is a SECOND load: the backend already loads the view's rows to format cell values, and
 *   this does not reuse the mounted embed's already-loaded data — it refetches out of band.
 * - There is currently NO row-count cap; a matrix/transposed view over a very large table will
 *   block the export for the duration of the stream. Matrix/transpose are usually summary/filtered
 *   views, so this is an edge case, but if it becomes a problem the cheapest guard is to skip the
 *   grid above a `totalRowsOnServer` threshold and let the backend fall back to its flat-table
 *   rendering (log what was skipped).
 */
import api from 'helpers/api'
import { ITableRow, ITableViewWithColumns } from 'types'
import { ISpreadsheetData, ITransposedResult } from 'components/spreadsheet/types'
import { ViewTypes } from 'components/spreadsheet/constants/const'
import { getMatrixData } from 'components/spreadsheet/helpers/matrix'
import { getTransposedData } from 'components/spreadsheet/helpers/transpose'
import { getSpreadsheetRowsStream } from 'components/spreadsheet/helpers/functions'
import { CompressedColumnStore } from 'components/spreadsheet/helpers/compressed-column-store'
import { extractEmbeddedViewsFromDocument } from './helpers'
import { ProcessData, ViewGrid, ViewGridCell, ViewGrids } from './types'

// Turn a matrix/transpose compute result into the wire grid. The same conversion works for both
// because getMatrixData and getTransposedData share the ITransposedResult shape (synthetic
// columns + synthetic rows + a coordinateMap back to the original cells).
export const resultToGrid = (result: ITransposedResult): ViewGrid => {
  const { columns, rows, coordinateMap, columnIdToIndex } = result

  const headers = columns.map((column) => column.name)
  const widths = columns.map((column) => column.width)

  const cells: ViewGridCell[][] = rows.map((row) =>
    columns.map((column, columnIndex) => {
      const coordinate = coordinateMap.get(`${row.publicId}:${column.publicId}`)
      if (coordinate && coordinate.originalRowId && coordinate.originalColumnId) {
        return { ref: [coordinate.originalRowId, coordinate.originalColumnId] }
      }

      // No source cell: a label/header-derived cell (use its literal value) or a blank
      // matrix sparse/collision cell (null).
      const dataIndex = columnIdToIndex.get(column.publicId) ?? columnIndex
      const value = row.rowData ? row.rowData[dataIndex] : null
      if (value == null) return null
      return { text: String(value) }
    })
  )

  return { headers, widths, cells }
}

// Load a view's rows into the minimal ISpreadsheetData that the pure pivot helpers read. This
// reproduces (out of band) what the mounted spreadsheet provider assembles, using the same
// standalone stream helper — no React, no mounted component required.
const loadSpreadsheetDataForView = async (
  tableId: string,
  viewId: string,
  viewDetails: ITableViewWithColumns,
  processId?: string
): Promise<ISpreadsheetData> => {
  const rows: ITableRow[] = []
  let columnIdToIndex = new Map<string, number>()
  let columnStore: CompressedColumnStore | null = null

  // isAdmin=false so the view's own filters/sort apply (export = the saved view the user sees).
  for await (const batch of getSpreadsheetRowsStream(false, tableId, viewId, viewDetails.columns, processId)) {
    columnIdToIndex = batch.columnIdToIndex
    columnStore = batch.columnStore
    rows.push(...batch.batchRows)
  }

  // Only the fields the pivot helpers read are populated; the rest of ISpreadsheetData is not
  // needed to compute the arrangement.
  return {
    viewDetails,
    columnIdToIndex,
    columnStore,
    rows,
    visibleBitmap: null,
    userConfiguration: { hiddenColumns: [] }
  } as unknown as ISpreadsheetData
}

const computeGridForView = async (
  tableId: string,
  viewId: string,
  viewDetails: ITableViewWithColumns,
  processId?: string
): Promise<ViewGrid | null> => {
  if (viewDetails.type === ViewTypes.TRANSPOSE) {
    const pivotColumnId = viewDetails.transposeSettings?.pivotColumnId
    if (!pivotColumnId) return null

    const spreadsheetData = await loadSpreadsheetDataForView(tableId, viewId, viewDetails, processId)
    // An export carries the whole view: the on-screen size limits don't apply.
    const result = getTransposedData(spreadsheetData, pivotColumnId, { maxColumns: Infinity })
    if (result.columns.length === 0) return null
    return resultToGrid(result)
  }

  if (viewDetails.type === ViewTypes.MATRIX) {
    const matrixSettings = viewDetails.matrixSettings
    if (
      !matrixSettings?.pivotColumnId ||
      !matrixSettings.rowLabelColumnIds?.length ||
      !matrixSettings.dataFieldColumnIds?.length
    ) {
      return null
    }

    const spreadsheetData = await loadSpreadsheetDataForView(tableId, viewId, viewDetails, processId)
    const result = getMatrixData(spreadsheetData, matrixSettings, { maxDataColumns: Infinity, maxCells: Infinity })
    if (result.columns.length === 0) return null
    return resultToGrid(result)
  }

  return null
}

// Cap on how many embedded views are processed at once. Each qualifying MATRIX/TRANSPOSE view
// streams its full result set into the browser, so unbounded parallelism could hold several large
// datasets in memory simultaneously. A small pool bounds peak memory; keeping it low costs little
// wall-clock because the msgpack decode / column-store build is single-threaded (see file header).
const MAX_CONCURRENT_VIEW_LOADS = 3

// Build the matrix/transpose grid layouts for every embedded view in the document. The pivot is
// computed here (frontend) and passed to the backend, which draws the tables and formats the
// referenced cell values. Non-matrix/transpose views and any view that fails to load are skipped
// — the backend then renders them via its normal flat-table path.
export const buildViewGrids = async (process: ProcessData, processId?: string): Promise<ViewGrids> => {
  const embeddedViews = extractEmbeddedViewsFromDocument(process)
  if (embeddedViews.length === 0) return {}

  const grids: ViewGrids = {}

  // Bounded worker pool: `cursor` is only read/incremented synchronously (before any await), so no
  // two workers ever claim the same view even though they run concurrently.
  let cursor = 0
  const worker = async (): Promise<void> => {
    while (cursor < embeddedViews.length) {
      const { tableId, viewId } = embeddedViews[cursor++]
      try {
        const viewDetails = (await api.getTableView(viewId)).data
        const grid = await computeGridForView(tableId, viewId, viewDetails, processId)
        if (grid) grids[viewId] = grid
      } catch {
        // Skip views that fail to load; the backend falls back to its flat-table rendering.
      }
    }
  }

  const workerCount = Math.min(MAX_CONCURRENT_VIEW_LOADS, embeddedViews.length)
  await Promise.all(Array.from({ length: workerCount }, () => worker()))

  return grids
}
